import React, { useState } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import { adminService } from "../../services/adminService";
import toast from "react-hot-toast";

interface ManualRefundModalProps {
  show: boolean;
  onHide: () => void;
  booking: any | null;
  onSuccess?: () => void;
}

const ManualRefundModal: React.FC<ManualRefundModalProps> = ({
  show,
  onHide,
  booking,
  onSuccess,
}) => {
  const [mrAmount, setMrAmount] = useState("");
  const [mrQty, setMrQty] = useState("1");
  const [refundMode, setRefundMode] = useState<"Cash" | "UPI" | "Card">(
    "Cash",
  );
  const [refundReason, setRefundReason] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setMrAmount("");
    setMrQty("1");
    setRefundMode("Cash");
    setRefundReason("");
    setReferenceNumber("");
    setError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onHide();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const amt = Number(mrAmount);
    const qty = Number(mrQty);

    if (!booking?.id) {
      setError("Booking not loaded");
      return;
    }
    if (!Number.isFinite(amt) || amt <= 0) {
      setError("MR Amount must be a positive number");
      return;
    }
    if (!Number.isInteger(qty) || qty <= 0) {
      setError("MR Quantity must be a positive integer");
      return;
    }
    if (!refundReason.trim()) {
      setError("Refund Reason is required");
      return;
    }
    if (qty > Number(booking.seatCount || booking.seat_count || 0)) {
      setError(
        `MR Qty (${qty}) cannot exceed ticket count (${booking.seatCount || booking.seat_count})`,
      );
      return;
    }

    setSubmitting(true);
    try {
      const r: any = await adminService.manualRefund(booking.id, {
        mrAmount: amt,
        mrQty: qty,
        refundMode,
        refundReason: refundReason.trim(),
        referenceNumber: referenceNumber.trim() || undefined,
      });
      if (r?.success) {
        toast.success(
          r?.message || `Manual refund of ₹${amt.toFixed(2)} recorded`,
        );
        reset();
        onHide();
        onSuccess?.();
      } else {
        setError(r?.error || "Failed to record manual refund");
      }
    } catch (e: any) {
      setError(
        e?.response?.data?.error || e?.message || "Failed to record manual refund",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const seatCount = Number(booking?.seatCount || booking?.seat_count || 0);

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi-cash-coin me-2"></i>
          Manual Refund (Counter)
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {booking && (
            <Alert variant="info" className="mb-3">
              <div className="small">
                <strong>Booking:</strong> {booking.id}
                <br />
                <strong>Customer:</strong>{" "}
                {booking.customerName ||
                  booking.userName ||
                  booking.user_name ||
                  booking.bookingDetails?.customer?.name ||
                  "-"}
                <br />
                <strong>Movie:</strong> {booking.movieTitle || booking.movie_title}
                <br />
                <strong>Cinema:</strong> {booking.cinemaName || booking.cinema_name}
                <br />
                <strong>Original Amount:</strong> ₹
                {Number(booking.totalAmount || booking.total_amount || 0).toFixed(
                  2,
                )}{" "}
                &nbsp;|&nbsp; <strong>Tickets:</strong> {seatCount}
              </div>
            </Alert>
          )}

          {error && <Alert variant="danger">{error}</Alert>}

          <div className="row g-3">
            <div className="col-md-6">
              <Form.Label>
                MR Amount (₹) <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={mrAmount}
                onChange={(e) => setMrAmount(e.target.value)}
                placeholder="e.g., 500.00"
                required
                autoFocus
              />
            </div>
            <div className="col-md-3">
              <Form.Label>
                MR Qty <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                min="1"
                max={seatCount || undefined}
                value={mrQty}
                onChange={(e) => setMrQty(e.target.value)}
                required
              />
              {seatCount > 0 && (
                <Form.Text className="text-muted">of {seatCount} ticket(s)</Form.Text>
              )}
            </div>
            <div className="col-md-3">
              <Form.Label>
                Refund Mode <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={refundMode}
                onChange={(e) =>
                  setRefundMode(e.target.value as "Cash" | "UPI" | "Card")
                }
              >
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Card">Card</option>
              </Form.Select>
            </div>
            <div className="col-12">
              <Form.Label>
                Refund Reason <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder='e.g., "Show cancelled", "Customer dispute", "Theatre technical issue"'
                required
              />
            </div>
            <div className="col-12">
              <Form.Label>Reference Number (optional)</Form.Label>
              <Form.Control
                type="text"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder="Offline receipt / trace number"
              />
            </div>
          </div>

          <Alert variant="warning" className="mt-3 mb-0 small">
            <i className="bi-exclamation-triangle me-1"></i>
            <strong>Heads up:</strong> This records a refund that has
            <em> already been given </em> at the cinema counter in cash. No
            payment gateway refund is initiated. The customer will be notified
            via WhatsApp, SMS and Email.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="success" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Recording…
              </>
            ) : (
              <>
                <i className="bi-check-lg me-1"></i>
                Save Manual Refund
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ManualRefundModal;
